import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';

import type { Action, Options, WsConnection } from './types';
import { error } from './actions';
import {
  DEFAULT_PREFIX,
  WEBSOCKET_CONNECT,
  WEBSOCKET_DISCONNECT,
  WEBSOCKET_SEND,
} from './actionTypes';
import ReduxWebSocket from './ReduxWebSocket';
import type { ReduxWebSocketOptions } from './ReduxWebSocket';
import { isRecord } from '@skynet/type-guards';

/**
 * Default middleware creator options.
 * @private
 */
const defaultOptions: Partial<Options> = {
  dateSerializer: undefined,
  prefix: DEFAULT_PREFIX,
  reconnectInterval: 2000,
  reconnectOnClose: false,
  reconnectOnError: true,
  serializer: JSON.stringify,
};

/**
 * Create a middleware.
 *
 * @param {Options} rawOptions
 *
 * @returns {Middleware}
 */
export default (rawOptions?: Options): Middleware => {
  const options = { ...defaultOptions, ...rawOptions };
  const prefix = options.prefix ?? DEFAULT_PREFIX;
  const { dateSerializer } = options;
  const actionPrefixExp = RegExp(`^${prefix}::`);

  // Build concrete options for ReduxWebSocket (constructor expects required fields).
  const wsOptions: ReduxWebSocketOptions = {
    prefix,
    exclusionGroup: options.exclusionGroup,
    reconnectInterval: options.reconnectInterval ?? 2000,
    reconnectOnClose: options.reconnectOnClose ?? false,
    reconnectOnError: options.reconnectOnError ?? true,
    onOpen: options.onOpen,
    serializer: options.serializer,
    deserializer: options.deserializer,
  };

  // Create a new redux websocket instance.
  const reduxWebsocket = new ReduxWebSocket(wsOptions);

  // Narrow guard for Action
  const isAction = (v: unknown): v is Action => isRecord(v) && typeof v.type === 'string';

  // Helper to coerce unknown into a Record safely
  const asRecord = (v: unknown): Record<string, unknown> | null => (isRecord(v) ? v : null);

  // Define the list of handlers (uniform function signature); handlers accept Action (we'll ensure runtime checks before calling)
  type HandlerKey = typeof WEBSOCKET_CONNECT | typeof WEBSOCKET_DISCONNECT | typeof WEBSOCKET_SEND;
  const handlers: Record<HandlerKey, (store: MiddlewareAPI, action: Action) => void> = {
    [WEBSOCKET_CONNECT]: (store, action) => {
      const rec = asRecord(action.payload);
      const rawUrl = rec?.['url'];
      const rawProtocols = rec?.['protocols'];
      const url = typeof rawUrl === 'string' ? rawUrl : '';
      const protocols = Array.isArray(rawProtocols)
        ? rawProtocols.filter((p): p is string => typeof p === 'string')
        : undefined;
      const payload: WsConnection = { url, protocols };
      reduxWebsocket.connect(store, { payload });
    },
    [WEBSOCKET_DISCONNECT]: () => {
      reduxWebsocket.disconnect();
    },
    [WEBSOCKET_SEND]: (store, action) => {
      reduxWebsocket.send(store, action);
    },
  };

  // Middleware function.
  return (store: MiddlewareAPI) => (next) => (action: unknown) => {
    // Narrow incoming action at runtime to avoid unsafe assertions
    if (!isRecord(action)) {
      return next(action);
    }

    const maybeAction = action;
    const { dispatch } = store;
    const actionType = typeof maybeAction.type === 'string' ? String(maybeAction.type) : undefined;

    // Prepare action copy for forwarding to next() — declare in outer scope so it's available regardless of branches
    let actionToNext: unknown = action;

    // Check if action type matches prefix
    if (actionType?.match(actionPrefixExp)) {
      const baseActionTypeRaw = String(actionType).replace(actionPrefixExp, '');
      let baseActionType: HandlerKey | null = null;
      if (
        baseActionTypeRaw === WEBSOCKET_CONNECT ||
        baseActionTypeRaw === WEBSOCKET_DISCONNECT ||
        baseActionTypeRaw === WEBSOCKET_SEND
      ) {
        baseActionType = baseActionTypeRaw;
      }

      // Don't mutate the original action; if timestamp needs serialization, clone with converted timestamp
      if (
        isAction(action) &&
        action.meta?.timestamp &&
        typeof action.meta.timestamp !== 'string' &&
        dateSerializer
      ) {
        const ts = action.meta.timestamp;
        const date = ts instanceof Date ? ts : new Date(String(ts));
        const serialized = String(dateSerializer(date));
        const newMeta: Record<string, unknown> = {
          ...(action.meta ?? {}),
          timestamp: serialized,
        };
        actionToNext = { ...(action as Record<string, unknown>), meta: newMeta };
      }

      const handler = baseActionType ? handlers[baseActionType] : undefined;
      if (handler && isAction(action)) {
        try {
          handler(store, action);
        } catch (err) {
          const thrown = err instanceof Error ? err : new Error(String(err));
          dispatch(error(action, thrown, prefix));
        }
      }
    }

    return next(actionToNext);
  };
};
